// The page pieces every module screen is built from (30-day list row 18): one header with the
// back button and the project picker, one empty state. Modules may not define their own
// (tools/check-modules.js refuses it) so every screen reads the same.
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Icon } from './Icon';
import type { Project } from './projects';

export interface HeadProps {
  title: string; sub?: ReactNode; right?: ReactNode;
  /** Where the back button goes: a route, or a handler when the screen has to confirm first. */
  back?: string; onBack?: () => void;
  /** With `projects` and `onPid`, the project picker sits under the title. */
  pid?: string; projects?: Project[]; onPid?: (id: string) => void;
}
export function Head({ title, sub, back, onBack, right, pid, projects, onPid }: HeadProps) {
  const nav = useNavigate();
  // The room picks the project (30-day rows 30 and 31), so a page no longer carries its own picker: it names the
  // project it is on beside the topic instead. Modules keep passing pid/projects/onPid; the picker simply is not drawn.
  void onPid;
  const name = pid && projects ? projects.find(p => p.id === pid)?.name ?? null : null;
  const subText = typeof sub === 'string' ? sub : null;
  const line = name && !(subText ?? '').includes(name) ? <>{name}{sub ? ' · ' : ''}{sub}</> : sub;
  return (
    <>
      <div className="flex items-center gap-2.5 pt-1.5">
        {(back || onBack) && <button onClick={onBack ?? (() => nav(back!))} className="w-9 h-9 rounded-full bg-surface border border-line grid place-items-center shrink-0" aria-label="Back"><Icon name="back" size={18} /></button>}
        <div className="min-w-0 flex-1"><h2 className="font-disp font-bold text-[26px] leading-none m-0 truncate">{title}</h2>{line && <div className="text-muted text-[12.5px] mt-1 truncate">{line}</div>}</div>
        {right}
      </div>
    </>
  );
}

export function Empty({ icon, title, text, action }: { icon: string; title: string; text: string; action?: ReactNode }) {
  return <div className="card text-center py-6"><div className="ic ic-acc mx-auto mb-2.5 w-12 h-12 rounded-[12px] border-0"><Icon name={icon} size={24} /></div><b className="font-head font-bold text-base block">{title}</b><p className="text-muted text-[13.5px] m-0 mb-2">{text}</p>{action}</div>;
}

/** Label / value rows, the way every record page lists its facts. */
export function KV({ rows }: { rows: Array<[string, ReactNode] | null | false> }) {
  return <div className="grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-1 text-[13.5px]">{rows.filter(Boolean).map((r, i) => { const [k, v] = r as [string, ReactNode]; return <div key={i} className="contents"><span className="text-muted">{k}</span><b className="font-semibold min-w-0 break-words">{v}</b></div>; })}</div>;
}
